import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { itemPath } from '../../utils/worldFormat';
import { ItemThumb } from '../../components/world/ItemSprite';
import { normalize } from '../../utils/text';
import { skillKindOf, skillNameOf } from '../../utils/skillItems';
import type { WorldData } from '../../types/world';
const PAGE = 40;
export function ItemsExplorer() {
  useEffect(() => {
    document.title = 'Ítems y drops · Guía MU Vanguard';
  }, []);
  return <div data-id="element-3372" data-mp="2xtibk76wzkzp">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Ítems y drops'
    }]} eyebrow="Explorador" title="Ítems y drops" data-id="element-3373" data-mp="2xspclse75zs4">
        <p className="mt-3 max-w-2xl text-base text-gray-300 leading-relaxed" data-id="element-3374" data-mp="2a234k1470fi4">
          Busca un ítem y mira de dónde sale: qué monstruo, evento o caja lo entrega y en qué mapa.
        </p>
      </WorldPageHeader>
      <WorldGate data-id="element-3375" data-mp="2rgvds1ua3laj">{w => <ItemList w={w} data-id="element-3376" data-mp="2whj74u5zce7x" />}</WorldGate>
    </div>;
}
function ItemList({
  w
}: {
  w: WorldData;
}) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const onlyVerified = params.get('verificados') === '1';
  const onlySkills = params.get('poderes') === '1';
  const [limit, setLimit] = useState(PAGE);
  const set = (k: string, v: string) => setParams(p => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v);else n.delete(k);
    return n;
  }, {
    replace: true
  });
  const list = useMemo(() => {
    const nq = normalize(q.trim());
    const variants = Array.from(w.variants.values()).filter(v => v.id !== v.itemId);
    const base = Array.from(w.items.values()).map(i => {
      const skill = skillNameOf(i);
      return {
        id: i.id,
        name: i.name,
        note: skill ? `${skillKindOf(i)} · enseña ${skill}` : i.availabilityNote,
        verified: i.hasVerifiedRoute,
        level: i.requiredLevel,
        skill
      };
    });
    const vars = variants.map(v => ({
      id: v.id,
      name: v.name,
      note: `Variante de ${w.items.get(v.itemId)?.name ?? v.itemId}`,
      verified: (w.idx.routesByVariant.get(v.id)?.length ?? 0) > 0,
      level: undefined as number | undefined,
      skill: undefined as string | undefined
    }));
    return [...base, ...vars].filter(i => (!nq || normalize(i.name).includes(nq) || !!i.skill && normalize(i.skill).includes(nq)) && (!onlyVerified || i.verified) && (!onlySkills || !!i.skill)).sort((a, b) => a.name.localeCompare(b.name));
  }, [w, q, onlyVerified, onlySkills]);
  useEffect(() => setLimit(PAGE), [q, onlyVerified, onlySkills]);
  return <div data-id="element-3377" data-mp="1k5dvozhdll94">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-3378" data-mp="1k4m0hproxpjd">
        <div className="flex h-12 flex-1 items-center gap-3 rounded-full border border-white/15 bg-[#0A1628] px-4 focus-within:border-[#D4AF37]/70" data-id="element-3379" data-mp="wfjlkpofyask">
          <Search className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-3380" data-mp="sd6g82dlwewb" />
          <input type="search" value={q} onChange={e => set('q', e.target.value)} placeholder="Nombre del ítem…" aria-label="Buscar ítem" className="min-w-0 flex-1 bg-transparent text-[15px] text-white placeholder:text-gray-500 focus:outline-none" data-id="element-3381" data-mp="i9rbtel21mar" />
        </div>
        <label className="flex h-12 cursor-pointer items-center gap-2.5 rounded-full border border-white/15 px-4 text-sm text-gray-200" data-id="element-3382" data-mp="daxzvqlf23cp">
          <input type="checkbox" checked={onlyVerified} onChange={e => set('verificados', e.target.checked ? '1' : '')} className="h-4 w-4 accent-[#D4AF37]" data-id="element-3383" data-mp="111k7xhvf7nmp" />
          Solo con ruta verificada
        </label>
        <label className="flex h-12 cursor-pointer items-center gap-2.5 rounded-full border border-white/15 px-4 text-sm text-gray-200" data-id="element-3384" data-mp="r1jj5ges8fb4">
          <input type="checkbox" checked={onlySkills} onChange={e => set('poderes', e.target.checked ? '1' : '')} className="h-4 w-4 accent-[#D4AF37]" data-id="element-3385" data-mp="w38wx3te372w" />
          Pergaminos y orbes
        </label>
      </div>

      <p className="mt-5 text-sm text-gray-500" data-id="element-3386" data-mp="tf4ll9shwl1">{list.length.toLocaleString('es-ES')} ítems</p>
      {list.length === 0 ? <p className="mt-3 rounded-2xl border border-white/10 p-6 text-gray-300" data-id="element-3387" data-mp="3s7t7jev2hp9e">No encontramos ítems con ese nombre en los archivos.</p> : <ul className="mt-3 grid gap-2.5 md:grid-cols-2" data-id="element-3388" data-mp="3n6vqx6pph3a0">
          {list.slice(0, limit).map(i => <li key={i.id} data-id="element-3389" data-mp="3i7sntrtad74o">
              <Link to={itemPath(i.id)} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-3390" data-mp="jrzu58w18twk">
                <ItemThumb id={i.id} name={i.name} data-id="element-3391" data-mp="9ttruq6bdlr6" />
                <span className="min-w-0 flex-1" data-id="element-3392" data-mp="9siq0vwfio2p">
                  <span className="block font-semibold text-white" data-id="element-3393" data-mp="4viggb0df36u">{i.name}</span>
                  <span className="mt-0.5 block truncate text-sm text-gray-400" data-id="element-3394" data-mp="2ukazk698cr6g">{i.note ?? (i.verified ? 'Con ruta verificada' : 'Sin ruta verificada')}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-3395" data-mp="2pnca410ih8el" />
              </Link>
            </li>)}
        </ul>}
      {list.length > limit && <div className="mt-6 flex justify-center" data-id="element-3396" data-mp="1a079cx97mew8">
          <button type="button" onClick={() => setLimit(l => l + PAGE * 2)} className="h-11 rounded-full border border-white/15 px-6 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-3397" data-mp="1f13ax4639oyh">
            Mostrar más ({list.length - limit} restantes)
          </button>
        </div>}
    </div>;
}